import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, GraduationCap, Globe2, Users } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { PARTNER_LOGOS } from "@/data/logos";
import { GALLERY } from "@/data/gallery";
import { useLang } from "@/i18n/LanguageContext";
import { usePageMeta } from "@/lib/usePageMeta";

const CAL_URL = "https://cal.com/saber-bellaaj-5ogauj/rdv-weyb.fr";

const PILLAR_LINKS = ["/solutions/developpement-international", "/solutions/programmes-appui-formation", "/solutions/export360", "/solutions/implantation-rh-international"];

const LANGUES = ["Français", "Anglais", "Arabe", "Italien"];

export default function APropos() {
    usePageMeta(
        { fr: "À propos de WEYB — Cabinet de développement international", en: "About WEYB — International development consultancy" },
        { fr: "WEYB, fondé par Saber Bellaaj : plus de 20 ans de terrain en commerce international. France, Tunisie, réseau Europe-Afrique-MENA.", en: "WEYB, founded by Saber Bellaaj: 20+ years in international trade. France, Tunisia, Europe-Africa-MENA network." }
    );
    const { t, prefix, lang } = useLang();
    const timeline = t("apropos.timeline");
    const pillars = t("apropos.pillars");
    const coaching = t("apropos.coachingList");
    const presence = t("apropos.presenceRows");

    return (
        <>
            <script type="application/ld+json">{JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Person",
                name: "Saber Bellaaj",
                jobTitle: "Fondateur & CEO",
                worksFor: { "@type": "Organization", name: "WEYB", url: "https://weyb.fr" },
                description: "Consultant senior en développement international, plus de 20 ans d'expérience terrain — Europe, Afrique, MENA.",
                knowsAbout: ["Développement international", "Stratégie export", "Formation de formateurs", "Monitoring & Evaluation", "Prospection B2B"],
            })}</script>
        <div data-testid="apropos-page">
            {/* Hero */}
            <section className="bg-white pt-32 md:pt-44 pb-20 md:pb-28">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Eyebrow index={t("apropos.heroEyebrowIndex")}>{t("apropos.heroEyebrow")}</Eyebrow>
                    <Reveal>
                        <h1 className="font-display text-4xl md:text-6xl font-light tracking-tight text-weyb-ink leading-[1.05] max-w-4xl">
                            {t("apropos.heroTitle")}
                        </h1>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="mt-8 text-lg md:text-2xl font-light leading-relaxed text-weyb-ink/75 max-w-3xl">
                            {t("apropos.heroIntro")}
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Histoire */}
            <section className="py-20 md:py-28 bg-[#F9FAFB] border-y border-gray-100">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Eyebrow index="01">{t("apropos.historyEyebrow")}</Eyebrow>
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-weyb-ink leading-tight max-w-2xl">
                            {t("apropos.historyTitle")}
                        </h2>
                    </Reveal>
                    <div className="mt-14 relative">
                        <span className="absolute left-[7px] md:left-1/2 top-0 bottom-0 w-px bg-gray-200" aria-hidden="true" />
                        <div className="space-y-14">
                            {timeline.map((tl, i) => (
                                <Reveal key={tl.year} delay={i * 0.08}>
                                    <div
                                        data-testid={`timeline-${i}`}
                                        className={`relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-16 pl-8 md:pl-0 ${i % 2 === 1 ? "md:text-left" : ""}`}
                                    >
                                        <span className="absolute left-0 md:left-1/2 top-2 -translate-x-1/2 md:-translate-x-1/2 h-[15px] w-[15px] rotate-45 bg-weyb-yellow" aria-hidden="true" />
                                        <div className={i % 2 === 1 ? "md:order-2" : "md:text-right"}>
                                            <span className="font-display text-4xl md:text-5xl font-light text-weyb-yellow">{tl.year}</span>
                                        </div>
                                        <div className={i % 2 === 1 ? "md:order-1 md:text-right" : ""}>
                                            <h3 className="font-display text-xl md:text-2xl font-medium text-weyb-ink">{tl.title}</h3>
                                            <p className="mt-3 text-sm md:text-base leading-relaxed text-gray-600 max-w-md md:inline-block">{tl.text}</p>
                                        </div>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Saber Bellaaj */}
            <section className="py-20 md:py-28 bg-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
                        <Reveal className="lg:col-span-6">
                            <div className="relative">
                                <span className="absolute -top-4 -left-4 w-16 h-16 border-t-2 border-l-2 border-weyb-yellow z-10" aria-hidden="true" />
                                <div className="overflow-hidden rounded-sm bg-[#F9FAFB]">
                                    <img
                                        src="/assets/saber-portrait.webp"
                                        alt={t("apropos.portraitAlt")}
                                        data-testid="apropos-saber-portrait"
                                        loading="lazy"
                                        className="w-full aspect-[16/10] object-cover object-[center_20%]"
                                    />
                                </div>
                                <span className="absolute -bottom-4 -right-4 w-16 h-16 border-b-2 border-r-2 border-weyb-yellow" aria-hidden="true" />
                            </div>
                        </Reveal>
                        <div className="lg:col-span-6">
                            <Eyebrow index="02">{t("apropos.founderEyebrow")}</Eyebrow>
                            <Reveal>
                                <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-weyb-ink leading-tight">
                                    Saber Bellaaj
                                    <span className="block mt-2 text-lg md:text-xl font-medium text-gray-500 tracking-normal">
                                        {t("apropos.founderRole")}
                                    </span>
                                </h2>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <p className="mt-6 text-base md:text-lg leading-relaxed text-gray-600">
                                    {t("apropos.founderBio")}
                                </p>
                            </Reveal>
                            <Reveal delay={0.16}>
                                <div className="mt-8 flex flex-wrap gap-2.5">
                                    {LANGUES.map((l) => (
                                        <span key={l} className="text-xs font-semibold uppercase tracking-[0.12em] text-weyb-ink border border-gray-200 px-4 py-2 rounded-sm">
                                            {l}
                                        </span>
                                    ))}
                                    <span className="text-xs font-semibold uppercase tracking-[0.12em] text-weyb-ink bg-weyb-yellow px-4 py-2 rounded-sm">
                                        {t("apropos.coachBadge")}
                                    </span>
                                </div>
                            </Reveal>
                            <Reveal delay={0.22}>
                                <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm font-medium text-weyb-ink" aria-label="Relation entre les entités">
                                    <span className="border border-weyb-ink px-4 py-2 rounded-sm">Saber Bellaaj</span>
                                    <ArrowRight size={14} className="text-weyb-yellow" aria-hidden="true" />
                                    <span className="border border-weyb-ink px-4 py-2 rounded-sm">WEYB</span>
                                    <ArrowRight size={14} className="text-weyb-yellow" aria-hidden="true" />
                                    <span className="border border-weyb-ink px-4 py-2 rounded-sm">{t("apropos.chainExpertises")}</span>
                                    <ArrowRight size={14} className="text-weyb-yellow" aria-hidden="true" />
                                    <span className="bg-weyb-yellow px-4 py-2 rounded-sm font-semibold">Export360</span>
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* Trois piliers */}
            <section className="py-20 md:py-28 bg-weyb-ink text-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Eyebrow index="03" dark>{t("apropos.pillarsEyebrow")}</Eyebrow>
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-2xl">
                            {t("apropos.pillarsTitle")}
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
                        {pillars.map((p, i) => (
                            <Reveal key={p.title} delay={i * 0.08} className="h-full">
                                <Link
                                    to={`${prefix}${PILLAR_LINKS[i]}`}
                                    data-testid={`pilier-${i}`}
                                    className="group flex flex-col h-full bg-weyb-ink hover:bg-white/5 p-8 transition-colors"
                                >
                                    <span className="font-display text-sm font-medium text-weyb-yellow">0{i + 1}</span>
                                    <h3 className="mt-4 font-display text-xl font-medium">{p.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-white/55 flex-1">{p.desc}</p>
                                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-weyb-yellow">
                                        {p.cta}
                                        <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                                    </span>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Coaching & formation + modèle agile */}
            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14">
                    <div>
                        <Eyebrow index="04">{t("apropos.coachingEyebrow")}</Eyebrow>
                        <Reveal>
                            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-weyb-ink leading-tight">
                                <GraduationCap size={30} strokeWidth={1.5} className="text-weyb-yellow mb-4" aria-hidden="true" />
                                {t("apropos.coachingTitle")}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <div className="mt-8 overflow-hidden rounded-sm">
                                <img
                                    src="/assets/terrain-partenaire-digital.jpg"
                                    alt="Saber Bellaaj en mission partenaire — écosystème digital, Tunisie — Saber Bellaaj on a partner mission — digital ecosystem, Tunisia"
                                    data-testid="apropos-coaching-image"
                                    loading="lazy"
                                    className="w-full aspect-[16/10] object-cover"
                                />
                            </div>
                        </Reveal>
                        <ul className="mt-8 space-y-3">
                            {coaching.map((c) => (
                                <Reveal key={c}>
                                    <li className="flex items-start gap-3 text-sm md:text-base text-gray-700">
                                        <span className="mt-[9px] h-1 w-1 shrink-0 bg-weyb-yellow" aria-hidden="true" />
                                        {c}
                                    </li>
                                </Reveal>
                            ))}
                        </ul>
                        <Reveal delay={0.1}>
                            <p className="mt-8 border-l-2 border-weyb-yellow pl-5 text-sm leading-relaxed text-gray-500">
                                {t("apropos.qualiopiNote")}
                            </p>
                        </Reveal>
                    </div>
                    <div>
                        <Eyebrow index="05">{t("apropos.agileEyebrow")}</Eyebrow>
                        <Reveal>
                            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-weyb-ink leading-tight">
                                <Users size={30} strokeWidth={1.5} className="text-weyb-yellow mb-4" aria-hidden="true" />
                                {t("apropos.agileTitle")}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <p className="mt-8 text-base leading-relaxed text-gray-600">
                                {t("apropos.agileText")}
                            </p>
                        </Reveal>
                        <Reveal delay={0.14}>
                            <div className="mt-8 bg-[#F9FAFB] border border-gray-200 rounded-sm p-7">
                                <Globe2 size={20} strokeWidth={1.5} className="text-weyb-yellow mb-4" aria-hidden="true" />
                                <h3 className="font-display text-lg font-medium text-weyb-ink">{t("apropos.presenceTitle")}</h3>
                                <div className="mt-4 space-y-3 text-sm text-gray-600">
                                    {presence.map((row, i) => (
                                        <p key={row.place} className={`flex justify-between gap-4 ${i < presence.length - 1 ? "border-b border-gray-100 pb-3" : ""}`}>
                                            <span className="font-semibold text-weyb-ink">{row.place}</span>
                                            <span className="text-right">{row.role}</span>
                                        </p>
                                    ))}
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Galerie terrain */}
            <section data-testid="gallery-section" className="py-20 md:py-28 bg-weyb-ink text-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Eyebrow index="06" dark>{t("apropos.galleryEyebrow")}</Eyebrow>
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-2xl">
                            {t("apropos.galleryTitle")}
                        </h2>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <p className="mt-5 text-sm text-white/55 max-w-2xl leading-relaxed">
                            {t("apropos.galleryNote")}
                        </p>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                        {GALLERY.map((g, i) => (
                            <Reveal key={g.src} delay={(i % 4) * 0.05} className="h-full">
                                <figure data-testid={`gallery-item-${i}`} className="group relative h-full overflow-hidden rounded-sm aspect-[4/3]">
                                    <img
                                        src={g.src}
                                        alt={g.caption[lang]}
                                        loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-weyb-ink/90 via-weyb-ink/50 to-transparent px-4 pt-10 pb-3 text-[11px] leading-snug text-white/85">
                                        {g.caption[lang]}
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Références */}
            <section className="py-20 md:py-28 bg-[#F9FAFB] border-y border-gray-100">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Eyebrow index="07">{t("apropos.refsEyebrow")}</Eyebrow>
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-weyb-ink leading-tight max-w-2xl">
                            {t("apropos.refsTitle")}
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-px bg-gray-200 border border-gray-200">
                        {PARTNER_LOGOS.slice(0, 10).map((logo) => (
                            <div key={logo.name} data-testid={`partner-logo-${logo.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className={`group ${logo.darkBg ? "bg-[#0E0E18]" : "bg-white"} flex items-center justify-center p-5 min-h-[90px]`}>
                                <img
                                    src={logo.src}
                                    alt={`Logo ${logo.name}`}
                                    loading="lazy"
                                    className={`${logo.size === "sq" ? "h-8" : "h-9"} w-auto max-w-[130px] object-contain grayscale opacity-50 transition-all duration-500 group-hover:grayscale-0 group-hover:opacity-100`}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-36 bg-weyb-ink text-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12 text-left">
                    <Reveal>
                        <h2 className="font-display text-4xl md:text-6xl font-light tracking-tight leading-[1.05] max-w-3xl">
                            {t("apropos.ctaTitle")}
                        </h2>
                    </Reveal>
                    <Reveal delay={0.12} className="mt-10 flex flex-wrap gap-4">
                        <a
                            href={CAL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="apropos-cta-booking"
                            className="inline-flex items-center gap-2 bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark px-8 py-4 rounded-sm text-sm font-semibold transition-colors"
                        >
                            {t("apropos.ctaPrimary")}
                            <ArrowUpRight size={16} />
                        </a>
                        <Link
                            to={`${prefix}/solutions`}
                            data-testid="apropos-cta-solutions"
                            className="inline-flex items-center gap-2 border border-white/25 text-white hover:border-weyb-yellow hover:text-weyb-yellow px-8 py-4 rounded-sm text-sm font-medium transition-colors"
                        >
                            {t("apropos.ctaSecondary")}
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
        </>
    );
}

